import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { ScanData } from 'pages/reporting/reports/raut/sections/subSections/indicationDetailsComponents/indicationDisplay/types'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseInspectionScanFilesProps {
  inspectionId: string | undefined
  fileName: string
}

/**
 * Return type for inspection scan files
 */
export interface InspectionScanFiles {
  /** S-scan image */
  sscanImageUrl?: string
  /** Top view - along-weld vs cross-weld */
  topView?: ScanData
  /** Side view - depth vs along-weld */
  sideView?: ScanData
}

/**
 * Hook to fetch and process scan files from the reporting directory
 * for files that don't have indications
 *
 * This hook:
 * - Fetches scan files from reporting/{fileName}/ directory
 * - Looks for sscan.png, topview.json, sideview.json
 * - Processes raw scan data
 * - Provides loading states and error handling
 *
 * @param props - Hook properties
 * @returns Processed scan data for S-scan image, top view, and side view
 */
export const useInspectionScanFiles = ({
  inspectionId,
  fileName,
}: UseInspectionScanFilesProps) => {
  const [scanData, setScanData] = useState<{
    sscanImageUrl?: string
    topView?: number[][]
    sideView?: number[][]
  }>({})

  const [error, setError] = useState<Error>()

  const cleanFileName = fileName.replace(/\.nde$/i, '')
  const cleanAndParseScanData = useCallback((text: string): number[][] => {
    try {
      const cleanedText = text
        .replace(/\bNaN\b/g, '0')
        .replace(/(\d+\.\d+)e-(\d+)/g, '0')
        .replace(/\s+/g, ' ')
        .trim()

      if (!cleanedText.startsWith('[[') || !cleanedText.endsWith(']]')) {
        throw new Error('Invalid data format')
      }

      const parsed = JSON.parse(cleanedText) as (
        | number
        | string
        | null
        | undefined
      )[][]

      return parsed.map((row) =>
        row.map((value) => {
          const num = Number(value)
          return Number.isFinite(num) ? num : 0
        })
      )
    } catch (e) {
      console.error('Error cleaning/parsing data:', e)
      return []
    }
  }, [])

  const { fileRefs, isLoading: fileRefsLoading } = useRolodexFileLS(
    inspectionId
      ? {
          entry_id: inspectionId,
          path: `reporting/${cleanFileName}/**`,
        }
      : undefined
  )

  const scanRefs = useMemo(() => {
    const findRef = (patterns: string[]) => {
      return fileRefs.find((ref) =>
        patterns.some((p) => {
          const refPath = ref.path.toLowerCase()
          const expectedPath = `reporting/${cleanFileName.toLowerCase()}/${p.toLowerCase()}`
          return refPath === expectedPath
        })
      )
    }

    return {
      sscanImage: findRef(['sscan.png']),
      topView: findRef(['topview.json']),
      sideView: findRef(['sideview.json']),
    }
  }, [fileRefs, cleanFileName])

  const urlRequests = useMemo(() => {
    if (!inspectionId) return []

    return Object.entries(scanRefs)
      .filter(([, ref]) => ref !== undefined)
      .map(([, ref]) => ({
        entry_id: inspectionId,
        path: ref!.path,
      }))
  }, [inspectionId, scanRefs])

  const { results: signedUrls, isLoading: urlsLoading } =
    useRolodexSignedURLs(urlRequests)

  /**
   * Fetch and process scan data when signed URLs are available
   */
  useEffect(() => {
    let isMounted = true

    const fetchScanData = async () => {
      try {
        const newScanData: typeof scanData = {}

        // Fetch top view and side view JSON data
        for (const scanType of ['topView', 'sideView'] as const) {
          const ref = scanRefs[scanType]
          if (!ref) continue

          const url = signedUrls.find(
            (urlResult) => urlResult.path === ref.path
          )?.signed_url

          if (url) {
            try {
              const response = await fetch(url)
              const text = await response.text()
              const processedData = cleanAndParseScanData(text)

              if (processedData.length > 0 && isMounted) {
                newScanData[scanType] = processedData
              }
            } catch (fetchError) {
              console.error(
                `Error fetching ${scanType} for file ${fileName}:`,
                fetchError
              )
            }
          }
        }

        // Get signed URL for S-scan image (no need to fetch, just store the URL)
        if (scanRefs.sscanImage) {
          const url = signedUrls.find(
            (urlResult) => urlResult.path === scanRefs.sscanImage!.path
          )?.signed_url
          if (url) {
            newScanData.sscanImageUrl = url
          }
        }

        if (isMounted) {
          setScanData(newScanData)
        }
      } catch (e) {
        console.error(
          `Error processing inspection scan data for file ${fileName}:`,
          e
        )
        if (isMounted) {
          setError(
            e instanceof Error ? e : new Error('Failed to fetch scan data')
          )
        }
      }
    }

    if (signedUrls.length > 0) {
      fetchScanData()
    }

    return () => {
      isMounted = false
    }
  }, [fileName, signedUrls, scanRefs, cleanAndParseScanData])

  const scanFiles: InspectionScanFiles = useMemo(
    () => ({
      sscanImageUrl: scanData.sscanImageUrl,
      topView: scanData.topView ? { data: scanData.topView } : undefined,
      sideView: scanData.sideView ? { data: scanData.sideView } : undefined,
    }),
    [scanData]
  )

  return {
    scanFiles,
    isLoading: fileRefsLoading || urlsLoading,
    error,
    hasScanData:
      !!scanData.sscanImageUrl || !!scanData.topView || !!scanData.sideView,
  }
}
